import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import PremiumReportingApi from '../../../api/PremiumReportingApi';
import { AuthData } from '../../../context/auth';
import {
	EndorsementContext,
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { useErrorContext } from '../../../context/ErrorProvider';
import { PremiumReportResponse } from '../../../types/premiumReports';
import AuxDataView from '../../auxData/AuxDataView';
import { StyledContentPane } from '../../common';
import DisabledOverlay from '../../DisabledOverlay';
import { useFastForm } from '../../fastForm';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import EndorsementLayout from '../../layouts/EndorsementLayout';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import ProtectedRoute from '../../ProtectedRoute';
import DocumentsView from '../Endorsements/DocumentsView';
import EndorsementPricePreview from '../Endorsements/EndorsementPricePreview';
import Exposures from '../Endorsements/Exposures';
import InvoicesView from '../Endorsements/InvoicesView';
import InvoiceView from '../Endorsements/InvoiceView';
import PolicyDetails from '../Endorsements/PolicyDetails';
import ReviewChanges from '../Endorsements/ReviewChanges';
import EndorsementSidebar from '../Sidebar/EndorsementSidebar';
import EndorsementExposureRoutes from './EndorsementExposureRoutes';

interface Props {
	endorsementData: EndorsementData;
	authData: AuthData;
}

// Route: '.../policy/:policyId/endorsements/:endorsementId'
const EndorsementRoutes = (props: Props) => {
	const errorData = useErrorContext();
	const { endorsementData, authData } = props;
	const { hasPermission } = authData;
	const navigate = useNavigate();
	const { setEndorsementUpdateObj, policy, endorsementUpdateObj, fields } =
		endorsementData;
	const locator = policy?.locator;
	const params = useParams();
	const { policyId, endorsementId } = params;
	const [loading, setLoading] = useState<boolean>(false);
	const methods = useFastForm(fields, {});
	const { reset } = methods;
	useEffect(() => {
		if (endorsementId) {
			const load = async () => {
				setLoading(true);
				await endorsementData.loadData(policyId, endorsementId);
				setLoading(false);
			};

			load();
		}

		return () => {
			setEndorsementUpdateObj(null);
		};
	}, [policyId, endorsementId]);

	const { data: premiumReportEndDates } = useQuery({
		queryKey: ['reportEndDates', locator, authData.auth.authorizationToken],
		enabled:
			!!policy &&
			!!policy?.premiumReportName &&
			hasPermission('premiumReport.fetch'),
		onError: async (error) => {
			errorData.handleErrorResponse(error, 'Failed to fetch premium reports');
			if (await handleUnauthorizedResponse(error as AxiosError, authData)) {
				return;
			}
		},
		queryFn: async () => {
			const premiumReportResponse = await PremiumReportingApi.getPremiumReports(
				authData.auth.authorizationToken,
				locator,
			);
			const premiumReports = premiumReportResponse.data;
			const issuedReports =
				premiumReports &&
				premiumReports.filter(
					(report: PremiumReportResponse) => report.state === 'issued',
				);
			const issuedReportEndDates =
				issuedReports &&
				issuedReports.map(
					(report: PremiumReportResponse) => report.endTimestamp,
				);
			return issuedReportEndDates;
		},
		retry: false,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		const populateForm = () => {
			if (fields) {
				const defaultEndorsementValues = getDefaultPolicyValues(
					fields,
					authData,
				);
				reset(defaultEndorsementValues);
			}
		};
		populateForm();
	}, [fields, reset, authData]);

	const renderMain = () => {
		if (loading || !policy || !endorsementUpdateObj) {
			return <LargeCenterSpinner />;
		}

		const defaultUrl =
			endorsementUpdateObj.state === 'application'
				? `policyDetails`
				: `price-breakdown`;

		return (
			<StyledContentPane>
				<EndorsementLayout>
					<Routes>
						<Route
							path='policyDetails'
							element={
								<PolicyDetails
									premiumReportEndDates={premiumReportEndDates || []}
								/>
							}
						/>
						<Route
							path='exposures/:exposureId/*'
							element={<EndorsementExposureRoutes />}
						/>
						<Route path='exposures' element={<Exposures />} />
						<Route path='review' element={<ReviewChanges />} />
						<Route
							path='price-breakdown'
							element={<EndorsementPricePreview />}
						/>
						<Route
							path='invoices/:invoiceId'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<InvoicesView />
								</ProtectedRoute>
							}
						/>
						<Route path='documents' element={<DocumentsView />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={endorsementId}
										entityType={'endorsement'}
									/>
								</ProtectedRoute>
							}
						/>
						<Route path='/' element={<Navigate replace to={defaultUrl} />} />
					</Routes>
				</EndorsementLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (loading || !policy || !endorsementUpdateObj) {
			return <DisabledOverlay />;
		}

		return <EndorsementSidebar />;
	};

	return (
		<FormProvider {...methods}>
			<EndorsementContext.Provider value={endorsementData}>
				<LeftSidebarLayout
					leftSidebarContent={renderSidebar()}
					mainContent={renderMain()}
				/>
			</EndorsementContext.Provider>
		</FormProvider>
	);
};

export default withEndorsementContext<Props>(EndorsementRoutes);
